// ─────────────────────────────────────────────
// KLOTHIQ — Bulk Product Import (Excel)
// Product-type-scoped template download (optional) → upload →
// client-side validation against real Master Config → preview
// with explicit NEW/UPDATE/ERROR classification → one atomic commit.
// Spec: .spec/features/FEAT-BULK-IMPORT-001-excel-import/spec.md
// ─────────────────────────────────────────────

import { Input } from '@/components/ui/FormControls';
import { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Download,
  Upload,
  FileSpreadsheet,
  Loader2,
  CheckCircle2,
  AlertTriangle,
  XCircle,
  ArrowRight,
  ArrowLeft,
  Info,
} from 'lucide-react';
import { toast } from 'sonner';
import PageHeader from '@/components/common/PageHeader';
import { useAuth } from '@/contexts/AuthContext';
import {
  useProductTypes,
  useProductTypeAttributes,
  useCategories,
  useTargetAudiences,
  useAgeGroups,
  useTeams,
  isValidUUID,
} from '@/hooks/useMasterConfig';
import { downloadImportTemplate } from '@/utils/bulkImportTemplate';
import {
  validateImportFile,
  buildCommitPayload,
  ValidationResult,
} from '@/utils/bulkImportValidation';
import { useOperations } from '@/hooks/useOperations';
import { useCatalogProfile } from '@/hooks/useCatalog';
import { useBulkImportCommit } from '@/hooks/useBulkImport';
import JerseyImportReviews from '@/components/products/JerseyImportReviews';
import JerseyImportEntry from '@/components/products/JerseyImportEntry';
import { useJerseyEntry } from '@/hooks/useJerseyEntry';
import { jerseyCodePreview, jerseySkuPreview } from '@klothiq/domain';
import { useOperationIntent } from '@/hooks/useOperationIntent';

type Step = 'select-type' | 'working' | 'preview' | 'importing' | 'result';

export default function ImportProductsPage() {
  const navigate = useNavigate();
  const { organization, role, can } = useAuth();
  const orgId = organization?.id;
  const canImport = role === 'owner' || role === 'admin';

  const { data: productTypes, isLoading: loadingTypes } = useProductTypes();
  const { data: categories } = useCategories();
  const { data: targetAudiences } = useTargetAudiences();
  const { data: ageGroups } = useAgeGroups();
  const { data: teams } = useTeams();

  const [step, setStep] = useState<Step>('select-type');
  const [selectedTypeId, setSelectedTypeId] = useState<string>('');
  const [workingMessage, setWorkingMessage] = useState('');
  const [fileName, setFileName] = useState('');
  const [validation, setValidation] = useState<ValidationResult | null>(null);
  const [commitResult, setCommitResult] = useState<{
    created: number;
    updated: number;
    variantsCreated: number;
    variantsUpdated: number;
    recovered: number;
  } | null>(null);
  const [commitError, setCommitError] = useState<string>('');
  const [isDragOver, setIsDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const commit = useBulkImportCommit();
  const importIntent = useOperationIntent('catalog-import');

  const selectedType = useMemo(
    () =>
      productTypes?.find((pt: any) => ((pt as any).id || pt.code || pt.name) === selectedTypeId),
    [productTypes, selectedTypeId],
  );
  const { data: legacyAttributes } = useProductTypeAttributes(selectedTypeId);
  const operations = useOperations();
  const catalog = useCatalogProfile(
    operations.data?.modules.catalog === 1 ? selectedTypeId : undefined,
  );
  const jerseyEnabled = catalog.data?.definition.jersey_identifiers?.enabled === true;
  const entry = useJerseyEntry(selectedTypeId);
  const entryEnabled = entry.data?.enabled === true;
  const dynamicAttributes =
    operations.data?.modules.catalog === 1
      ? catalog.data?.definition.fields.map((f) => ({
          catalog_field: true,
          code: f.code,
          name: f.label,
          is_variant_level: f.level === 'variant',
          is_required: f.required && !f.when,
          input_type: f.type,
          options: f.options?.filter((o) => o.active !== false).map((o) => o.value),
        }))
      : legacyAttributes;
  const [failedGroups, setFailedGroups] = useState<{ product_code: string; error: string }[]>([]);

  useEffect(() => {
    if (!canImport) {
      navigate('/products', { replace: true });
    }
  }, [canImport, navigate]);

  if (!canImport) return null;

  const handleDownloadTemplate = () => {
    if (!selectedType) return;
    if (operations.data?.modules.catalog === 1 && !catalog.data?.schema_version_id) {
      toast.error('Publish this category setup before importing new products.');
      return;
    }
    const typeCode = (selectedType as any).code || selectedType.name;
    downloadImportTemplate(selectedType.name, typeCode, dynamicAttributes ?? [], {
      categories: categories ?? [],
      targetAudiences: targetAudiences ?? [],
      ageGroups: ageGroups ?? [],
      teams: teams ?? [],
    });
    toast.success(`Template downloaded for ${selectedType.name}`);
  };

  const handleFileUpload = async (file: File) => {
    if (!selectedType || !orgId) return;
    if (catalog.data?.definition.jersey_entry?.enabled && !entryEnabled) {
      toast.error(
        'Optional jersey entry is still loading or unavailable. Retry its settings before importing.',
      );
      return;
    }
    if (operations.data?.modules.catalog === 1 && !catalog.data?.schema_version_id) {
      toast.error('Publish this category setup before importing.');
      return;
    }

    if (!isValidUUID((selectedType as any).id)) {
      toast.error(
        'Product Types are not fully configured for this organization yet. Set them up in Settings before importing.',
      );
      return;
    }

    setFileName(file.name);
    setStep('working');
    setWorkingMessage('Reading file…');

    try {
      const typeCode = (selectedType as any).code || selectedType.name;
      setWorkingMessage('Validating against your product settings…');
      const result = await validateImportFile(
        file,
        { id: (selectedType as any).id, name: selectedType.name, code: typeCode },
        (dynamicAttributes ?? []) as never,
        orgId,
        { jerseyIdentifiers: jerseyEnabled || entryEnabled },
      );
      if (catalog.data && !catalog.data.legacy) {
        for (const g of result.groups) {
          for (const f of catalog.data.definition.fields) {
            const targets =
              f.level === 'product' ? [g.attributes] : g.variants.map((v) => v.attributes);
            for (const a of targets) {
              const raw = a[f.code];
              if (raw === undefined || raw === '') continue;
              if (f.type === 'number') {
                const n = Number(raw);
                if (!Number.isFinite(n))
                  result.errors.push({
                    row: g.sourceRows[0] || 2,
                    column: f.label,
                    message: 'Enter a valid number',
                  });
                else a[f.code] = n;
              }
              if (f.type === 'boolean') {
                if (!['true', 'false', 'yes', 'no'].includes(String(raw).toLowerCase()))
                  result.errors.push({
                    row: g.sourceRows[0] || 2,
                    column: f.label,
                    message: 'Use Yes or No',
                  });
                else a[f.code] = ['true', 'yes'].includes(String(raw).toLowerCase());
              }
              if (f.type === 'multiselect')
                a[f.code] = String(raw)
                  .split(';')
                  .map((v) => v.trim())
                  .filter(Boolean);
            }
          }
        }
      }
      setValidation(result);
      setStep('preview');
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Failed to read the file';
      toast.error(msg);
      setStep('select-type');
    }
  };

  const handleConfirmImport = async () => {
    if (catalog.data?.definition.jersey_entry?.enabled && !entryEnabled) {
      toast.error('Optional jersey entry is unavailable. Keep the preview and retry later.');
      return;
    }
    if (!can(entryEnabled ? 'imports.manage' : 'imports.commit')) {
      toast.error('Your login can review imports but cannot commit them.');
      return;
    }
    if (!validation || !selectedType) return;
    if (jerseyEnabled && !entryEnabled) {
      try {
        const config = catalog.data!.definition.jersey_identifiers!;
        if (operations.data?.modules.jersey_identifiers !== 1)
          throw new Error('The jersey identifier database update is required.');
        for (const group of validation.groups) {
          const freshVariants = group.variants.filter((v) => v.match === 'new');
          if (!freshVariants.length && group.match !== 'new') continue;
          if (
            !group.jersey_review?.confirmed ||
            group.jersey_review.configuration_version_id !== catalog.data?.schema_version_id
          )
            throw new Error(`Review SKU details for ${group.name}.`);
          const code = jerseyCodePreview(
            config,
            group.jersey_review.details,
            group.existing_id ? group.product_code : undefined,
          );
          freshVariants.forEach((v) =>
            jerseySkuPreview(config, group.jersey_review!.details, code, v.color || '', v.size),
          );
        }
      } catch (e) {
        toast.error((e as Error).message);
        return;
      }
    }
    setStep('importing');
    setCommitError('');
    try {
      const payload = buildCommitPayload((selectedType as any).id, fileName, validation.groups);
      const result = await commit.mutateAsync({
        ...payload,
        schema_version_id: catalog.data?.schema_version_id,
        ...(entryEnabled
          ? {
              jersey_entry_version: 2,
              configuration_version_id: entry.data!.configuration_version_id,
            }
          : {}),
      } as never);
      setFailedGroups(
        result.groups
          ?.filter((g) => !g.success)
          .map((g) => ({
            product_code:
              g.product_code ||
              validation.groups.find((group) => group.group_key === g.group_key)?.name ||
              'Unsaved product group',
            error: g.error || 'Import was not saved',
          })) || [],
      );
      setCommitResult({
        created: result.products_created,
        updated: result.products_updated,
        variantsCreated: result.variants_created,
        variantsUpdated: result.variants_updated,
        recovered: result.recovered_groups ?? 0,
      });
      setStep('result');
      if (result.groups?.some((g) => !g.success))
        toast.warning('Import completed with unsaved groups. Review the results.');
      else
        toast.success(
          `Imported ${result.products_created} new / updated ${result.products_updated} products`,
        );
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Import failed — no changes were made';
      setCommitError(msg);
      setStep('result');
    }
  };

  const handleDownloadErrorReport = () => {
    if (!validation || validation.errors.length === 0) return;
    const header = 'Row,Column,Message\n';
    const rows = validation.errors
      .map(
        (e) =>
          `${e.row},"${(e.column ?? '').replace(/"/g, '""')}","${e.message.replace(/"/g, '""')}"`,
      )
      .join('\n');
    const blob = new Blob([header + rows], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `import-errors-${fileName.replace(/\.[^.]+$/, '')}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const resetFlow = () => {
    setStep('select-type');
    setSelectedTypeId('');
    setFileName('');
    setValidation(null);
    setCommitResult(null);
    setCommitError('');
    setIsDragOver(false);
  };

  // Drag-and-drop handlers for the upload zone
  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragOver(false);
      const file = e.dataTransfer.files?.[0];
      if (file && file.name.endsWith('.xlsx')) {
        handleFileUpload(file);
      } else {
        toast.error('Please drop a .xlsx file');
      }
    },
    [selectedType, orgId],
  );

  return (
    <div className="mx-auto max-w-5xl pb-16">
      <PageHeader
        title="Import Products & Inventory"
        description="Select a product type, optionally download the template, then upload your filled-in file."
        backTo="/products"
      />

      {/* ── Step: select product type + optional template download + upload ── */}
      {step === 'select-type' && (
        <div className="space-y-4">
          {/* Product Type Selection */}
          <div className="rounded-xl border border-border bg-card p-6 shadow-sm space-y-4">
            <h2 className="text-sm font-bold uppercase tracking-wider text-muted-foreground">
              Step 1 — Select Product Type
            </h2>
            <p className="text-sm text-muted-foreground">
              The template's columns depend on the product type you pick — e.g. Jersey asks for Team
              and Player, a Hoodie template won't.
            </p>
            {loadingTypes ? (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" /> Loading product types…
              </div>
            ) : (
              <div className="flex flex-wrap gap-2">
                {(productTypes ?? []).map((pt: any) => {
                  const id = pt.id || pt.code || pt.name;
                  const isSelected = id === selectedTypeId;
                  return (
                    <button
                      key={id}
                      type="button"
                      onClick={() => setSelectedTypeId(id)}
                      className={`rounded-lg border px-4 py-2 text-sm font-medium transition-colors ${
                        isSelected
                          ? 'border-primary bg-primary/10 text-primary'
                          : 'border-border bg-background text-foreground hover:bg-accent'
                      }`}
                    >
                      {pt.name}
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          {/* Template Download (Optional) + Upload Zone — shown only when a type is selected */}
          {selectedTypeId && (
            <>
              {/* Optional Template Download */}
              <div className="rounded-xl border border-border bg-card p-5 shadow-sm">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                  <div className="space-y-1">
                    <h3 className="text-sm font-semibold text-foreground flex items-center gap-2">
                      <Download className="h-4 w-4 text-primary" />
                      Download Template
                      <span className="text-[10px] font-medium uppercase tracking-wider px-1.5 py-0.5 rounded bg-muted text-muted-foreground border border-border">
                        Optional
                      </span>
                    </h3>
                    <p className="text-xs text-muted-foreground">
                      If you don't have the template yet, download it first. If you already have it
                      filled in, skip to upload below.
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={handleDownloadTemplate}
                    className="inline-flex items-center gap-2 rounded-lg border border-border bg-background px-4 py-2 text-sm font-medium text-foreground shadow-xs transition-colors hover:bg-accent shrink-0"
                  >
                    <Download className="h-4 w-4" /> Download .xlsx Template
                  </button>
                </div>
              </div>

              {/* Upload Zone — always visible */}
              <div className="rounded-xl border border-border bg-card p-5 shadow-sm space-y-4">
                <h3 className="text-sm font-bold uppercase tracking-wider text-muted-foreground">
                  Step 2 — Upload Your File
                </h3>
                <div
                  className={`rounded-xl border-2 border-dashed p-8 text-center transition-colors ${
                    isDragOver
                      ? 'border-primary bg-primary/5'
                      : 'border-border bg-card/50 hover:bg-card/80'
                  }`}
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop}
                >
                  <Upload
                    className={`h-10 w-10 mx-auto mb-3 transition-colors ${isDragOver ? 'text-primary' : 'text-muted-foreground'}`}
                  />
                  <h3 className="text-base font-semibold text-foreground">
                    {isDragOver ? 'Drop your file here' : 'Upload your filled-in template'}
                  </h3>
                  <p className="text-xs text-muted-foreground mt-1 mb-4">
                    Drag & drop your .xlsx file here, or click the button below
                  </p>
                  <label className="inline-flex items-center gap-2 px-5 py-2.5 text-sm font-semibold rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 cursor-pointer shadow-sm transition-colors">
                    <FileSpreadsheet className="h-4 w-4" /> Choose .xlsx File
                    <Input
                      ref={fileInputRef}
                      type="file"
                      accept=".xlsx"
                      className="hidden"
                      onChange={(e) => {
                        const f = e.target.files?.[0];
                        if (f) handleFileUpload(f);
                        e.target.value = '';
                      }}
                      helpLabel="Choose .xlsx File"
                    />
                  </label>
                </div>
                <div className="flex items-start gap-2 rounded-lg bg-muted/50 border border-border/60 px-3 py-2.5">
                  <Info className="h-4 w-4 text-muted-foreground shrink-0 mt-0.5" />
                  <p className="text-xs text-muted-foreground leading-relaxed">
                    Make sure the file matches the <strong>{selectedType?.name}</strong> template
                    format. The system will validate column names and data automatically before
                    importing.
                  </p>
                </div>
              </div>
            </>
          )}
        </div>
      )}

      {/* ── Step: working (parsing / validating) ── */}
      {step === 'working' && (
        <div className="rounded-xl border border-border bg-card p-10 text-center space-y-3">
          <Loader2 className="h-8 w-8 animate-spin mx-auto text-primary" />
          <p className="text-sm text-muted-foreground">{workingMessage}</p>
        </div>
      )}

      {/* ── Step: preview ── */}
      {step === 'preview' && validation && (
        <>
          {entryEnabled && validation && (
            <JerseyImportEntry
              profileId={selectedTypeId}
              groups={validation.groups}
              onChange={(key, group) =>
                setValidation((current) =>
                  current
                    ? {
                        ...current,
                        groups: current.groups.map((g) => (g.group_key === key ? group : g)),
                      }
                    : current,
                )
              }
            />
          )}
          {jerseyEnabled && !entryEnabled && (
            <JerseyImportReviews
              profileId={selectedTypeId}
              groups={validation.groups}
              onChange={(key, review) =>
                setValidation((current) =>
                  current
                    ? {
                        ...current,
                        groups: current.groups.map((g) =>
                          g.group_key === key ? { ...g, jersey_review: review } : g,
                        ),
                      }
                    : current,
                )
              }
            />
          )}
          <PreviewStep
            validation={validation}
            fileName={fileName}
            onBack={() => setStep('select-type')}
            onConfirm={handleConfirmImport}
            onDownloadErrors={handleDownloadErrorReport}
          />
        </>
      )}

      {/* ── Step: importing ── */}
      {step === 'importing' && (
        <div className="rounded-xl border border-border bg-card p-10 text-center space-y-3">
          <Loader2 className="h-8 w-8 animate-spin mx-auto text-primary" />
          <p className="text-sm text-muted-foreground">Importing — please don't close this tab…</p>
        </div>
      )}

      {/* ── Step: result ── */}
      {step === 'result' && (
        <div className="rounded-xl border border-border bg-card p-8 text-center space-y-4">
          {commitError ? (
            <>
              <XCircle className="h-10 w-10 text-destructive mx-auto" />
              <p className="text-sm font-semibold text-foreground">
                Import failed — no changes were made
              </p>
              <p className="text-xs text-muted-foreground max-w-md mx-auto">{commitError}</p>
              <button
                type="button"
                className="min-h-11 text-sm underline"
                onClick={() =>
                  void importIntent
                    .recover('commit')
                    .then((result) => {
                      if (!result) {
                        toast.info(
                          'No completed import found. Keep the same details when retrying.',
                        );
                        return;
                      }
                      toast.success(
                        'The previous import result was recovered. Refresh Products before starting another import.',
                      );
                    })
                    .catch((e) => toast.error(e.message))
                }
              >
                Recover previous import result
              </button>
              <button
                type="button"
                onClick={() => setStep('preview')}
                className="inline-flex items-center gap-2 rounded-lg border border-border px-4 py-2 text-sm font-medium hover:bg-accent"
              >
                Back to Preview
              </button>
            </>
          ) : (
            <>
              <CheckCircle2 className="h-10 w-10 text-emerald-500 mx-auto" />
              <p className="text-sm font-semibold text-foreground">
                Imported {commitResult?.created} new product(s) ({commitResult?.variantsCreated}{' '}
                variants), updated {commitResult?.updated} product(s) (
                {commitResult?.variantsUpdated} variants)
              </p>
              {!!commitResult?.recovered && (
                <p className="text-sm text-muted-foreground">
                  Recovered {commitResult.recovered} previously saved group(s). These are excluded
                  from the new import counts.
                </p>
              )}
              {failedGroups.length > 0 && (
                <div
                  role="alert"
                  className="space-y-2 rounded-xl border border-destructive/30 p-4 text-left text-sm"
                >
                  <h3 className="font-semibold">
                    {failedGroups.length} product groups were not saved
                  </h3>
                  <p>
                    Successful groups remain saved. Correct these groups and revalidate the file
                    before importing again. Existing variants do not receive opening stock again.
                  </p>
                  {failedGroups.map((g, i) => (
                    <p key={i}>
                      {g.product_code}: {g.error}
                    </p>
                  ))}
                </div>
              )}
              <div className="flex items-center justify-center gap-3">
                <button
                  type="button"
                  onClick={() => navigate('/products')}
                  className="inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground hover:bg-primary/90"
                >
                  View Products
                </button>
                <button
                  type="button"
                  onClick={resetFlow}
                  className="inline-flex items-center gap-2 rounded-lg border border-border px-4 py-2 text-sm font-medium hover:bg-accent"
                >
                  Import Another File
                </button>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

// ── Preview sub-component ───────────────────────────────────────

function PreviewStep({
  validation,
  fileName,
  onBack,
  onConfirm,
  onDownloadErrors,
}: {
  validation: ValidationResult;
  fileName: string;
  onBack: () => void;
  onConfirm: () => void;
  onDownloadErrors: () => void;
}) {
  const newGroups = validation.groups.filter((g) => g.match === 'new');
  const updateGroups = validation.groups.filter((g) => g.match === 'update');
  const newVariantCount = newGroups.reduce(
    (sum, g) => sum + g.variants.filter((v) => v.match === 'new').length,
    0,
  );
  const updateVariantCount = validation.groups.reduce(
    (sum, g) => sum + g.variants.filter((v) => v.match === 'update').length,
    0,
  );
  const errorRowCount = new Set(validation.errors.map((e) => e.row)).size;

  return (
    <div className="space-y-4">
      <div className="rounded-xl border border-border bg-card p-4 flex items-center justify-between">
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <FileSpreadsheet className="h-4 w-4" /> {fileName}
        </div>
        <button
          type="button"
          onClick={onBack}
          className="inline-flex items-center gap-1.5 text-sm text-primary hover:underline"
        >
          <ArrowLeft className="h-3.5 w-3.5" /> Upload a different file
        </button>
      </div>

      <div className="rounded-xl border border-border bg-card p-5 space-y-2">
        <h2 className="text-sm font-bold uppercase tracking-wider text-muted-foreground">
          Step 2 — Preview
        </h2>
        <p className="text-sm text-foreground">
          Import <strong>{newGroups.length}</strong> new product(s) (
          <strong>{newVariantCount}</strong> variants), update{' '}
          <strong>{updateGroups.length}</strong> product(s) (<strong>{updateVariantCount}</strong>{' '}
          variants).
          {errorRowCount > 0 && (
            <>
              {' '}
              <strong className="text-destructive">{errorRowCount}</strong> row(s) will be skipped
              due to errors.
            </>
          )}
        </p>
        {validation.warnings.length > 0 && (
          <p className="text-xs text-amber-600 dark:text-amber-400">
            {validation.warnings.length} non-blocking warning(s) — see below.
          </p>
        )}
      </div>

      {/* Products table */}
      <div className="rounded-xl border border-border bg-card overflow-hidden shadow-sm">
        <div className="overflow-x-auto max-h-[28rem]">
          <table className="w-full text-xs text-left">
            <thead className="bg-muted/50 text-muted-foreground uppercase text-[10px] tracking-wider sticky top-0">
              <tr>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3">Product Code</th>
                <th className="px-4 py-3">Name</th>
                <th className="px-4 py-3">Variants</th>
                <th className="px-4 py-3">Changes</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {validation.groups.map((g) => (
                <tr key={g.group_key} className="hover:bg-muted/30">
                  <td className="px-4 py-2.5">
                    {g.match === 'new' ? (
                      <Badge
                        color="emerald"
                        icon={<CheckCircle2 className="h-3 w-3" />}
                        label="NEW"
                      />
                    ) : (
                      <Badge
                        color="blue"
                        icon={<ArrowRight className="h-3 w-3" />}
                        label="UPDATE"
                      />
                    )}
                    {g.isInactiveExisting && (
                      <div className="mt-1 text-[10px] text-amber-600 dark:text-amber-400">
                        inactive — won't reactivate
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-2.5 font-mono">{g.product_code || 'Assigned on save'}</td>
                  <td className="px-4 py-2.5">{g.name}</td>
                  <td className="px-4 py-2.5">
                    {g.variants.length} ({g.variants.filter((v) => v.match === 'new').length} new,{' '}
                    {g.variants.filter((v) => v.match === 'update').length} update)
                    {g.variants.some((v) => v.ignoredOpeningStock) && (
                      <div className="text-[10px] text-amber-600 dark:text-amber-400">
                        stock qty ignored on matched rows
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-2.5">
                    {g.diffs.length === 0 ? (
                      <span className="text-muted-foreground">—</span>
                    ) : (
                      <ul className="space-y-0.5">
                        {g.diffs.map((d) => (
                          <li key={d.field}>
                            <span className="font-medium">{d.field}:</span> "{d.oldValue}" → "
                            {d.newValue}"
                          </li>
                        ))}
                      </ul>
                    )}
                  </td>
                </tr>
              ))}
              {validation.groups.length === 0 && (
                <tr>
                  <td colSpan={5} className="px-4 py-8 text-center text-muted-foreground">
                    No importable rows — see errors below.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Errors table */}
      {validation.errors.length > 0 && (
        <div className="rounded-xl border border-destructive/30 bg-destructive/5 overflow-hidden">
          <div className="flex items-center justify-between px-4 py-3 border-b border-destructive/20">
            <h3 className="text-sm font-bold text-destructive flex items-center gap-2">
              <AlertTriangle className="h-4 w-4" /> {validation.errors.length} Error(s)
            </h3>
            <button
              type="button"
              onClick={onDownloadErrors}
              className="text-xs text-primary hover:underline"
            >
              Download error report (CSV)
            </button>
          </div>
          <div className="overflow-x-auto max-h-64">
            <table className="w-full text-xs text-left">
              <thead className="bg-muted/40 text-muted-foreground uppercase text-[10px]">
                <tr>
                  <th className="px-4 py-2">Row</th>
                  <th className="px-4 py-2">Column</th>
                  <th className="px-4 py-2">Message</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {validation.errors.map((e, idx) => (
                  <tr key={idx}>
                    <td className="px-4 py-2 font-mono">{e.row}</td>
                    <td className="px-4 py-2">{e.column ?? '—'}</td>
                    <td className="px-4 py-2">{e.message}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {validation.warnings.length > 0 && (
        <div className="rounded-xl border border-amber-500/30 bg-amber-500/5 p-4">
          <h3 className="text-sm font-bold text-amber-700 dark:text-amber-400 flex items-center gap-2 mb-2">
            <AlertTriangle className="h-4 w-4" /> Warnings (non-blocking)
          </h3>
          <ul className="text-xs space-y-1 text-foreground/80">
            {validation.warnings.map((w, idx) => (
              <li key={idx}>
                Row {w.row}: {w.message}
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="flex justify-end">
        <button
          type="button"
          disabled={validation.groups.length === 0}
          onClick={onConfirm}
          className="inline-flex items-center gap-2 rounded-lg bg-primary px-6 py-2.5 text-sm font-semibold text-primary-foreground shadow-sm transition-colors hover:bg-primary/90 disabled:opacity-50"
        >
          Import {newGroups.length} new / update {updateGroups.length} products{' '}
          <ArrowRight className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}

function Badge({
  color,
  icon,
  label,
}: {
  color: 'emerald' | 'blue';
  icon: React.ReactNode;
  label: string;
}) {
  const cls =
    color === 'emerald'
      ? 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/20'
      : 'bg-blue-500/10 text-blue-600 dark:text-blue-400 border-blue-500/20';
  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-bold border ${cls}`}
    >
      {icon} {label}
    </span>
  );
}
